import React, { useEffect, useRef, useState } from 'react';
import './HomeScreen.css';
import { useNavigate } from 'react-router-dom';
import { useAuth, useTransactions, useUI, useTracking } from '../hooks';
import { Transaction, Tracking } from '../types';
import Text from '../components/Text/Text';
import ActionButton from '../components/ActionButton/ActionButton';
import TransactionItem from '../components/TransactionItem/TransactionItem';
import IconBox from '../images/icons/Icon-box.png';
import IconCurrency from '../images/icons/Icon-currency-exchange.png';
import IconHandshake from '../images/icons/Icon-handshake.png';
import IconMoneyBill from '../images/icons/Icon-money-bill-wave.png';
import LabeledValue from '../components/LabeledValue';
import { CountdownTimer } from '../components/ReleaseFunds/CountdownTimer';
import { safeParseTimeSpanToHours } from '../utils/timeCalculations';

const HomeScreen: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, isLoading: authLoading, isUserDataLoading, userDataError, isRetrying, retryAttempt, maxRetries, refreshCurrentUser } = useAuth();
  const { transactions, getTransactions, getTransactionTotals, transactionTotals, releasePayment, isLoading, error } = useTransactions();
  const { trackingInfo, getTrackingInfo } = useTracking();
  const { showToast } = useUI();
  const [isReleasingFunds, setIsReleasingFunds] = useState<string | null>(null);
  const [isLoadingTotals, setIsLoadingTotals] = useState(false);
  
  // Use refs to track if data has been fetched
  const hasLoadedTransactions = useRef(false);
  const hasLoadedTotals = useRef(false);
  
  // Fetch transactions only once on component mount
  // We intentionally omit dependencies to prevent infinite loops
  // The ref ensures we only fetch once regardless of re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Only fetch if we haven't already loaded transactions
    if (!hasLoadedTransactions.current) {
      const fetchTransactions = async () => {
        try {
          await getTransactions();
          // Mark as loaded
          hasLoadedTransactions.current = true;
        } catch (error) {
          showToast('error', 'Failed to load transactions');
        }
      };
      
      fetchTransactions();
    }
  }, []); // Empty dependency array ensures this only runs once on mount
  
  // Fetch transaction totals only once on component mount
  useEffect(() => {
    // Only fetch if we haven't already loaded totals
    if (!hasLoadedTotals.current) {
      const fetchTotals = async () => {
        try {
          setIsLoadingTotals(true);
          await getTransactionTotals();
          // Mark as loaded
          hasLoadedTotals.current = true;
        } catch (error) {
          showToast('error', 'Failed to load transaction totals');
        } finally {
          setIsLoadingTotals(false);
        }
      };
      
      fetchTotals();
    }
  }, [getTransactionTotals, showToast]); // Include dependencies
  
  
  // Fetch tracking information for transactions with trackingId
  useEffect(() => {
    const fetchTrackingInfo = async () => {
      try {
        // Defensive check to ensure transactions is an array
        if (!Array.isArray(transactions)) {
          console.error('HomeScreen: transactions is not an array:', transactions);
          return;
        }
        
        console.log('HomeScreen: Processing transactions for tracking info:', transactions);
        
        // Get tracking info for transactions with trackingId
        const transactionsWithTracking = transactions.filter(t => t.trackingId) || [];
        
        for (const transaction of transactionsWithTracking) {
          if (transaction.trackingId && !trackingInfo[transaction.trackingId]) {
            await getTrackingInfo(transaction.trackingId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch tracking information:', error);
      }
    };
    
    // Ensure transactions exists and is an array before proceeding
    if (Array.isArray(transactions) && transactions.length > 0) {
      fetchTrackingInfo();
    } else {
      console.log('HomeScreen: No transactions to fetch tracking for:', transactions);
    }
  }, [transactions, trackingInfo, getTrackingInfo]);
  
  // Navigate to Pay Someone screen
  const handlePaySomeoneClick = () => {
    navigate('/pay');
  };
  
  // Navigate to Withdraw screen
  const handleWithdrawClick = () => {
    navigate('/withdraw');
  };
  
  // Navigate to Package Tracking screen
  const handleTrackingClick = (transactionId?: string) => {
    if (transactionId) {
      console.log('Navigating to tracking page with transaction ID:', transactionId);
      
      // Navigate to PackageTrackingScreen with the transaction ID
      navigate(`/tracking/add/${transactionId}`);
    } else {
      // Navigate to the general tracking page when no transaction ID is provided
      console.log('Navigating to tracking page without transaction ID');
      navigate('/tracking/add');
    }
  };
  
  // Navigate to Release Funds screen
  const handleReleaseFundsClick = () => {
    // If there are transactions pending fund release, navigate to the first one
    if (buyerTransactionsPendingFundRelease.length > 0) {
      const transaction = buyerTransactionsPendingFundRelease[0];
      console.log('Release funds button clicked, navigating to transaction:', transaction.id);
      navigate(`/release-funds/${transaction.id}`, {
        state: {
          fromHome: true,
          transactionData: transaction
        }
      });
    } else {
      // Otherwise, navigate to transactions page
      navigate('/transactions');
    }
  };
  
  // Release funds for a specific transaction
  const handleReleaseFunds = async (transactionId: string) => {
    try {
      setIsReleasingFunds(transactionId);
      await releasePayment(transactionId);
      showToast('success', 'Funds released successfully');
      setIsReleasingFunds(null);
    } catch (error) {
      showToast('error', 'Failed to release funds');
      setIsReleasingFunds(null);
    }
  };
  
  // Navigate to Transactions screen
  const handleViewMoreClick = () => {
    navigate('/transactions');
  };
  
  // Calculate total amount in escrow
  const totalInEscrow = Array.isArray(transactions)
    ? transactions
        .filter(t => t.status === 'in_escrow')
        .reduce((sum, t) => sum + t.totalAmount, 0)
    : 0;
  
  // Helper functions to determine transaction status
  const isBuyerTransactionPendingFundRelease = (transaction: Transaction, currentUserId: string): boolean => {
    return transaction.buyerId === currentUserId && transaction.status === 'in_escrow';
  };

  const isSellerTransactionRequiringTracking = (transaction: Transaction, currentUserId: string, trackingInfo: Record<string, Tracking>): boolean => {
    return transaction.sellerId === currentUserId &&
           transaction.status === 'in_escrow' &&
           (!transaction.trackingId ||
            !trackingInfo[transaction.trackingId] ||
            trackingInfo[transaction.trackingId]?.status === 'not_provided');
  };

  // Helper function to determine if a package is in a terminal state
  const isPackageInTerminalState = (tracking: Tracking | undefined): boolean => {
    if (!tracking) return false;
    return ['delivered', 'collected'].includes(tracking.status);
  };

  // Helper function to determine transaction styling
  const getTransactionStatusStyle = (transaction: Transaction, trackingInfo: Record<string, Tracking>): string => {
    if (!transaction.trackingId || !trackingInfo[transaction.trackingId]) {
      return 'text-danger'; // Red: No tracking information
    }
    
    const tracking = trackingInfo[transaction.trackingId];
    
    if (tracking && isPackageInTerminalState(tracking)) {
      return 'text-warning'; // Orange: Tracking provided + package in terminal state + funds unreleased
    }
    
    return 'text-success'; // Green: Tracking information provided
  };

  const addHours = (date: Date, hoursToAdd: number | string): Date => {
    const newDate = new Date(date); // Create a new Date object to avoid modifying the original
    
    // Parse the hoursToAdd if it's a TimeSpan string using centralized utility
    const hours = safeParseTimeSpanToHours(hoursToAdd);
    
    // Convert hours to milliseconds (including decimal values)
    const milliseconds = hours * 60 * 60 * 1000;
    
    // Add milliseconds to the timestamp
    newDate.setTime(newDate.getTime() + milliseconds);
    return newDate;
  };

  // Filter transactions for buyer transactions pending fund release
  const buyerTransactionsPendingFundRelease = Array.isArray(transactions)
    ? transactions.filter(
        transaction => isBuyerTransactionPendingFundRelease(transaction, currentUser?.id || '')
      )
    : [];

  // Filter transactions for seller transactions requiring tracking
  const sellerTransactionsRequiringTracking = Array.isArray(transactions)
    ? transactions.filter(
        transaction => isSellerTransactionRequiringTracking(transaction, currentUser?.id || '', trackingInfo)
      )
    : [];
  
  return (
    <div className="home-container">
      <div className="wallet-cards">
        <div className="wallet-card pending-card">
          <LabeledValue
            label='Total Pending Release'
            value={isLoadingTotals ? 'Loading...' : `$${(transactionTotals?.totalPendingRelease || 0).toFixed(2)}`}
            className='text-body text-h2'
            rightAligned={true}
          />
          <div className="card-description">
            <Text className="text-caption">Funds not yet available</Text>
          </div>
        </div>
        
        <div className="wallet-card withdrawable-card">
          <LabeledValue
            label='Total Withdrawable'
            value={isLoadingTotals ? 'Loading...' : `$${(transactionTotals?.totalWithdrawable || 0).toFixed(2)}`}
            className='text-body text-h2'
            rightAligned={true}
          />
          <div className="card-description">
            <div className="withdrawable-card-content">
              <Text className="text-caption">Funds available for withdrawal</Text>
              {!isLoadingTotals && (transactionTotals?.totalWithdrawable ?? 0) > 0 && (
                <button
                  className="withdraw-button"
                  onClick={handleWithdrawClick}
                >
                  Withdraw
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="escrow-card-section">
         <div className="section-header">
          <Text className="text-body"> Your VeriPay ID</Text>
        </div>
        {(() => {
          // Clear separation of loading states
          const isAuthenticating = authLoading;
          const isUserDataLoading_local = isUserDataLoading;
          const hasValidUserId = currentUser?.userId;
          const shouldShowLoading = isAuthenticating || isUserDataLoading_local;
          const shouldShowUserIdPrompt = currentUser && !currentUser.hasSetUserId && !shouldShowLoading;
          const shouldShowUserId = hasValidUserId && !shouldShowLoading;
          
          // Handle error state with retry button
          if (userDataError && !shouldShowLoading) {
            return (
              <div className="error-indicator">
                <LabeledValue
                  label=''
                  value="Unable to load VeriPay ID"
                  className='veripay-id text-error'
                />
                <div className="retry-section" style={{ marginTop: '8px', textAlign: 'center' }}>
                  <button
                    onClick={async () => {
                      console.log('HomeScreen: Manual retry triggered');
                      await refreshCurrentUser();
                    }}
                    disabled={isUserDataLoading}
                    style={{
                      fontSize: '12px',
                      padding: '6px 16px',
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: isUserDataLoading ? 'not-allowed' : 'pointer',
                      opacity: isUserDataLoading ? 0.6 : 1,
                      marginBottom: '4px'
                    }}
                  >
                    {isUserDataLoading ? 'Retrying...' : 'Try Again'}
                  </button>
                  <div style={{
                    fontSize: '11px',
                    color: '#666',
                    marginTop: '4px'
                  }}>
                    Check your connection and try again
                  </div>
                </div>
              </div>
            );
          }
          
          // Handle loading state with retry information
          if (shouldShowLoading) {
            const loadingText = isRetrying
              ? `Fetching... (${retryAttempt + 1}/${maxRetries})`
              : "Loading...";
              
            return (
              <div>
                <LabeledValue
                  label=''
                  value=""
                  isLoading={true}
                  loadingText={loadingText}
                  className='veripay-id'
                />
              </div>
            );
          }
          
          // Handle new user prompt
          if (shouldShowUserIdPrompt) {
            return (
              <div className="user-id-prompt">
                <LabeledValue
                  label=''
                  value="Set up your VeriPay ID"
                  className='veripay-id text-warning'
                />
              </div>
            );
          }
          
          // Handle existing user with VeriPay ID
          if (shouldShowUserId) {
            return (
              <div>
                <LabeledValue
                  label=''
                  value={currentUser.userId}
                  copyable={true}
                  className='veripay-id'
                  onCopy={() => showToast('success', 'User ID copied to clipboard')}
                />
              </div>
            );
          }
          
          // Fallback - should rarely be reached
          return (
            <div>
              <LabeledValue
                label=''
                value="VeriPay ID not available"
                className='veripay-id text-muted'
              />
            </div>
          );
        })()}
      </div>
      
      {/* <div className="escrow-card-section">
        <div className="section-header">
          <Text className="text-body">Receiving Payments</Text>
        </div>
        
        <ActionButton
          icon={<img src={IconBox} alt="Tracking details" />}
          label="Provide tracking details"
          className="action-button-icon-left"
          onClick={() => handleTrackingClick()}
        />

        <ActionButton
          icon={<img src={IconHandshake} alt="User profile" />}
          label="Your user profile"
          className="action-button-icon-left"
          onClick={() => handleTrackingClick()}
        />
      </div> */}
      
      <div className="escrow-card-section">
        <div className="section-header">
          <Text className="text-body">Payments</Text>
        </div>
        
        <div className="action-row">
          <ActionButton
            icon={<img src={IconMoneyBill} alt="Pay Someone" />}
            label="Pay Someone"
            className="action-half-width"
            onClick={handlePaySomeoneClick}
          />
          
          {/* <ActionButton
            icon={<img src={IconCurrency} alt="Release funds" />}
            label="Release funds"
            className="action-half-width"
            onClick={handleReleaseFundsClick}
          /> */}
        </div>
      </div>
      {/* Transactions Pending Fund Release Section */}
      {buyerTransactionsPendingFundRelease.length > 0 && (
        <div className="escrow-card-section">
          <div className="section-header">
            <Text className="text-body">Funds to be Released:</Text>
          </div>
          
          <div className="transaction-list">
            {isLoading ? (
              <Text>Loading transactions...</Text>
            ) : error ? (
              <Text className="text-error">Failed to load transactions</Text>
            ) : buyerTransactionsPendingFundRelease.length === 0 ? (
              <Text>No pending transactions</Text>
            ) : (
              <>
                {buyerTransactionsPendingFundRelease.map((transaction) => {
                  const statusStyle = getTransactionStatusStyle(transaction, trackingInfo);
                  const tracking = transaction.trackingId ? trackingInfo[transaction.trackingId] : undefined;
                  const showReleaseButton = tracking && isPackageInTerminalState(tracking);
        
                  let countdownTimerExpiredText = 'Expired';
                  let statusText = 'Payment in escrow';
                  if (!transaction.trackingId || !trackingInfo[transaction.trackingId]) {
                    statusText = 'No tracking information';
                    countdownTimerExpiredText = 'Pending tracking info.';
                  } else if (tracking && isPackageInTerminalState(tracking)) {
                    statusText = 'Ready to release funds';
                    countdownTimerExpiredText = 'Funds auto. released';
                  } else {
                    statusText = 'Tracking information provided';
                  }

                  if (transaction.status)
                  
                  return (
                    <TransactionItem
                      key={transaction.id}
                      role="Buyer"
                      itemDescription={transaction.itemDescription}
                      itemPrice={`$${transaction.originalAmount.toFixed(2)}`}
                      priceTagStyle="tag-left"
                      status={<Text className={`text-caption ${statusStyle}`}>{statusText}</Text>}
                      action={
                        <button
                          className="tracking-timer-tag" title="The time remaining before funds will be automatically released."
                        >
                          <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
                            <circle cx="12" cy="12" r="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 7v5l3 3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="12" cy="12" r="1" strokeWidth="0" fill="currentColor"/>
                          </svg>
                          
                          <CountdownTimer 
                            targetDate={new Date(transaction.autoReleaseTime ? transaction.autoReleaseTime : 0)} postTimeText='left' expiredText={countdownTimerExpiredText}
                          />
                        </button>
                      }
                      onClick={() => {
                        console.log('TransactionItem clicked, navigating to release funds transaction:', transaction.id);
                        // Add state to prevent unnecessary re-renders
                        navigate(`/release-funds/${transaction.id}`, {
                          state: {
                            fromHome: true,
                            transactionData: transaction
                          }
                        });
                      }}
                    />
                  );
                })}
                
                {buyerTransactionsPendingFundRelease.length > 0 && (
                  <button className="view-more-button" onClick={handleViewMoreClick}>
                    <Text className="text-body">View More</Text>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
      
      {/* Transactions Requiring Tracking Section */}
      {sellerTransactionsRequiringTracking.length > 0 && (
        <div className="escrow-card-section">
          <div className="section-header">
            <Text className="text-body">Tracking info required:</Text>
          </div>
          
          <div className="transaction-list">
            {isLoading ? (
              <Text>Loading transactions...</Text>
            ) : error ? (
              <Text className="text-error">Failed to load transactions</Text>
            ) : sellerTransactionsRequiringTracking.length === 0 ? (
              <Text>No transactions requiring tracking</Text>
            ) : (
              <>
                {sellerTransactionsRequiringTracking.map((transaction) => (
                  <TransactionItem
                    key={transaction.id}
                    role="Recipient"
                    itemPrice={`$${transaction.originalAmount.toFixed(2)}`}
                    itemDescription={transaction.itemDescription}
                    priceTagStyle="tag-left"
                    status={<Text className="text-caption text-danger">Tracking information needed</Text>}
                    action={
                      <button
                        className="tracking-timer-tag" title="The time remaining to provide tracking information. Click to provide tracking information."
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
                          <circle cx="12" cy="12" r="9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M12 7v5l3 3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="12" cy="12" r="1" strokeWidth="0" fill="currentColor"/>
                        </svg>
                        <CountdownTimer
                          targetDate={ addHours(new Date(), transaction.remainingHoursForTracking || 0) } postTimeText='left'
                        />
                      </button>
                    }
                    onClick={() => handleTrackingClick(transaction.id) }
                  />
                ))}
                
                {sellerTransactionsRequiringTracking.length > 0 && (
                  <button className="view-more-button" onClick={handleViewMoreClick}>
                    <Text className="text-body">View More</Text>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;